import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { Checkbox, Link, Tooltip } from 'components/patterns'
import { useSelector } from 'hooks/redux'
import AdvancedSearch from 'components/AdvancedSearch'
import strings from 'utils/strings'
import { updateSearchResultItem } from 'actions/search'
import { extractFeatureId } from 'utils/uriHelpers'
import { formatMinDiagnosticVariant } from 'utils/feature'
import { FeatureRevisionSelector } from 'components/feature/FeatureRevision/FeatureRevisionSelector'
import { hasRight } from 'selectors/authorization'
import { VIEW_FEATURE_REVISION } from 'constants/authorization'
import {
  FEATURE,
  FAST_FEATURES,
  VALID_STATUSES,
  restrictedModules,
} from 'utils/constants'
import { displayModuleName } from 'i18n/displayConstants'
import { isArray, isString } from 'utils/types'
import {
  getSwitchesByModuleForVehicle,
  getLogicAndTimerTotalsAndLimitsByModule,
  getCanInputsByModuleForVehicle,
  getDb35mCode,
  getTruckBuildDate,
  getShowSwitchIndicatorsForVehicle,
} from 'selectors/truck'
import { getSearchResults } from 'selectors/search'
import { buildVehicleValidationFns } from 'utils/featureValidation/checks'

const hasSameFeatureIdentity = (left, right) => {
  if (left.partNumber && right.partNumber)
    return left.partNumber === right.partNumber

  return left.uri === right.uri
}

export const getProspectiveSelectedFeatures = (
  selectedFeatures,
  previousFeature,
  revisedFeature
) =>
  selectedFeatures.map((selectedFeature) =>
    hasSameFeatureIdentity(selectedFeature, previousFeature)
      ? revisedFeature
      : selectedFeature
  )

const AddRemoveFeatureSearchAndResultsTable = (props) => {
  const {
    serialNumber,
    features = [],
    onSelected,
    onSelectedRevision,
    onSelectAll,
    selected,
    modules,
    authenticatedModuleNames,
    showFastFeatureFilters,
  } = props

  const dispatch = useDispatch()
  const showSwitchIndicators = useSelector(getShowSwitchIndicatorsForVehicle, {
    serialNumber,
  })
  const canViewRevision = useSelector(hasRight, VIEW_FEATURE_REVISION)
  const collectionName = !showFastFeatureFilters ? FEATURE : FAST_FEATURES

  const totalsAndLimits = useSelector(getLogicAndTimerTotalsAndLimitsByModule, {
    serialNumber,
  })

  const switchIndicatorsByModule = useSelector(getSwitchesByModuleForVehicle, {
    serialNumber,
  })

  const canInputsByModule = useSelector(getCanInputsByModuleForVehicle, {
    serialNumber,
  })

  const db35mCode = useSelector(getDb35mCode, { serialNumber })
  const buildDate = useSelector(getTruckBuildDate, { serialNumber })

  const searchResultsFromStore = useSelector(getSearchResults, collectionName)

  // Transform search results to:
  // 1. Add selected property for row highlighting
  // 2. Merge matching revision data to show correct counts on initial load
  const searchResults = useMemo(() => {
    if (!searchResultsFromStore || !Array.isArray(searchResultsFromStore))
      return searchResultsFromStore
    return searchResultsFromStore.map((record) => {
      // Find the matching revision in the revisions array
      const matchingRevision = record.revisions?.find(
        (rev) => (rev.revision || rev.release) === record.revision
      )

      return {
        ...record,
        // Merge matching revision data if found (includes complexQueryCount, timerQueryCount, pinCount, status, etc.)
        ...(matchingRevision || {}),
        // Preserve the top-level switchIndicators from the search result rather than
        // the revision's switchIndicators (which uses a broader DB query and can
        // over-count by including non-switch-indicator outputs). The top-level value
        // is computed by FeatureUtilities.GetSwitchIndicators which uses the precise
        // SwitchIndicatorIds logic — matching what the vehicle endpoint returns
        // after commit. (#504271)
        switchIndicators: record.switchIndicators,
        // Derive the correct id from the matching revision's URI so feature links
        // navigate to the current revision, not the oldest one
        id:
          (matchingRevision?.uri
            ? extractFeatureId(matchingRevision.uri)
            : null) || record.id,
        selected: selected.some((s) => s.uri === record.uri),
      }
    })
  }, [searchResultsFromStore, selected])

  const validationContext = useMemo(
    () => ({
      authenticatedModuleNames,
      modules,
      displayModuleName,
      restrictedModules,
      features,
      totalsAndLimits,
      canInputsByModule,
      switchIndicatorsByModule,
      db35mCode,
      buildDate,
    }),
    [
      authenticatedModuleNames,
      modules,
      features,
      totalsAndLimits,
      canInputsByModule,
      switchIndicatorsByModule,
      db35mCode,
      buildDate,
    ]
  )

  // All validation functions in order — defined in utils/featureValidation/checks.js
  // Note: isObsoleteFeature removed since checkStatus already handles all non-valid statuses
  const validationFns = buildVehicleValidationFns({
    ...validationContext,
    selectedFeatures: selected,
  })

  // Get all selectable features from search results
  const selectableFeatures = useMemo(() => {
    if (!searchResults || !Array.isArray(searchResults)) return []
    // Evaluate Select All cumulatively so selections cannot collectively exhaust
    // a module even when each candidate fits against the installed base alone.
    return searchResults.reduce(
      ({ selectable, staged }, record) => {
        const candidateValidationFns = buildVehicleValidationFns({
          ...validationContext,
          selectedFeatures: staged,
        })
        if (!candidateValidationFns.every((validateFn) => !validateFn(record)))
          return { selectable, staged }

        const alreadyStaged = staged.some((stagedFeature) =>
          hasSameFeatureIdentity(stagedFeature, record)
        )
        return {
          selectable: [...selectable, record],
          staged: alreadyStaged ? staged : [...staged, record],
        }
      },
      { selectable: [], staged: selected }
    ).selectable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, validationContext, selected])

  // Calculate header checkbox state
  const selectedCount = selected.length
  const selectableCount = selectableFeatures.length
  const allSelected = selectableCount > 0 && selectedCount === selectableCount
  const someSelected = selectedCount > 0 && selectedCount < selectableCount
  const indeterminate = someSelected

  // Handle select all click
  const handleSelectAllClick = () => {
    if (allSelected) {
      // Deselect all
      onSelectAll([], false)
    } else {
      // Select all selectable features
      onSelectAll(selectableFeatures, true)
    }
  }

  // Handle revision selection - updates search result with new revision data
  const handleRevisionSelect = useCallback(
    (revisionData, feature) => {
      // Extract ID from URI (e.g., "/features/1000013363" -> "1000013363")
      const revisionId = extractFeatureId(revisionData.uri)

      // Exclude switchIndicators from the revision payload — the top-level feature's
      // switchIndicators (computed by GetSwitchIndicators with the precise
      // SwitchIndicatorIds set) is authoritative. The per-revision value comes from
      // a broader DB LIKE query that can over-count by including non-switch-indicator
      // outputs. Omitting it here prevents the Redux store record from being
      // overwritten with the incorrect count. (#504271)
      // eslint-disable-next-line no-unused-vars
      const { switchIndicators: _omit, ...revisionFields } = revisionData

      const revisedFeature = {
        ...feature,
        ...revisionFields,
        id: revisionId,
        revision: revisionData.revision || revisionData.release,
        switchIndicators: feature.switchIndicators,
      }

      // Dispatch action to update the search result item with the selected revision data
      dispatch(
        updateSearchResultItem(collectionName, feature.id, {
          // Spread revision data (includes complexQueryCount, timerQueryCount, pinCount, etc.)
          // switchIndicators is intentionally excluded — see comment above
          ...revisedFeature,
        })
      )

      // Keep the parent's selected item synchronized with the fully merged revision.
      // A selected revision that fails any vehicle validation is removed instead.
      const isCurrentlySelected = selected.some((selectedFeature) =>
        hasSameFeatureIdentity(selectedFeature, feature)
      )
      if (isCurrentlySelected) {
        // Validate the set that would be selected after the revision change. A
        // revision has a distinct URI, so retaining the prior revision here
        // incorrectly counts its resources alongside the candidate's.
        const prospectiveSelected = getProspectiveSelectedFeatures(
          selected,
          feature,
          revisedFeature
        )
        const prospectiveValidationFns = buildVehicleValidationFns({
          ...validationContext,
          selectedFeatures: prospectiveSelected,
        })
        const invalid =
          !VALID_STATUSES.includes(revisedFeature.status) ||
          prospectiveValidationFns.some((validateFn) =>
            validateFn(revisedFeature)
          )
        onSelectedRevision(feature, revisedFeature, invalid)
      }
    },
    [dispatch, collectionName, selected, onSelectedRevision, validationContext]
  )

  // Header checkbox component
  const SelectAllCheckbox =
    selectableCount > 0 ? (
      <Checkbox
        checked={allSelected}
        indeterminate={indeterminate}
        onChange={handleSelectAllClick}
      />
    ) : null

  const renderCustomCheckbox = (validationsFns) => (text, record) => {
    const marked = selected.some((i) => i.uri === record.uri)
    const errStrings = validationsFns.reduce(
      (validationFns, validateRecord) => {
        const valError = validateRecord(record)
        let errorMessages = []

        if (valError) {
          if (isArray(valError)) {
            errorMessages = [
              ...errorMessages,
              ...valError.filter((str) => !!str).map((err) => err),
            ]
          }
          if (isString(valError)) {
            errorMessages = [...errorMessages, valError]
          }
        }
        return [...validationFns, ...errorMessages]
      },
      []
    )

    // Show all validation errors, deduplicated to avoid showing the same message twice
    const displayErrs = [...new Set(errStrings)]
    const errList = displayErrs.length ? (
      <ul style={{ width: '400px', paddingLeft: '14px', margin: 0 }}>
        {displayErrs.map((str) => (
          <li key={str}>{str}</li>
        ))}
      </ul>
    ) : null

    const disable = errStrings.length > 0
    const RadioBubble = (
      <Checkbox
        checked={marked}
        className="link"
        name="constant"
        disabled={disable}
        onChange={() => onSelected(record)}
      />
    )
    return errStrings ? (
      <Tooltip overlayClassName="autoWidth" title={errList} placement="right">
        {RadioBubble}
      </Tooltip>
    ) : (
      RadioBubble
    )
  }

  const getTableColumns = () => [
    {
      title: SelectAllCheckbox,
      dataIndex: 'id',
      render: renderCustomCheckbox(validationFns),
      width: 42,
      key: `radio`,
    },
    {
      title: strings.feature_no,
      dataIndex: 'partNumber',
      ...{
        render: (partNumber, record) => (
          <Link to={`/features/${record.id}`} target="_blank">
            {partNumber}
          </Link>
        ),
      },
      width: 150,
    },
    ...(canViewRevision
      ? [
          {
            title: strings.revision_short_dot,
            dataIndex: 'revision',
            render: (revision, record) => (
              <FeatureRevisionSelector
                revision={revision}
                feature={record}
                onSelect={(revisionData) =>
                  handleRevisionSelect(revisionData, record)
                }
                navigateOnSelect={false}
                collectionName={collectionName}
              />
            ),
            width: 60,
          },
        ]
      : []),
    {
      title: strings.description,
      dataIndex: 'description',
    },
    {
      title: strings.ecu,
      dataIndex: 'moduleName',
      width: 70,
      render: displayModuleName,
    },
    {
      title: strings.minimum_diagnostic_variant,
      dataIndex: 'minCompatibleVariant',
      width: 70,
      render: formatMinDiagnosticVariant,
    },
    {
      title: strings.master_no,
      dataIndex: 'masterNumber',
      width: 70,
    },
    {
      title: strings.release_no,
      dataIndex: 'releaseNumber',
      width: 70,
    },
    {
      title: strings.status,
      dataIndex: 'status',
      width: 70,
    },
    {
      title: <span>{strings.logic_sets_used} </span>,
      dataIndex: 'complexQueryCount',
      width: 55,
    },
    {
      title: <span>{strings.timers_used}</span>,
      dataIndex: 'timerQueryCount',
      width: 55,
    },
    {
      title: <span>{strings.can_inputs_used}</span>,
      dataIndex: 'canInputs',
      width: 55,
      render: (canInputs) => (
        <span>{!canInputs ? strings.n_a : canInputs.length}</span>
      ),
    },
    ...(showSwitchIndicators
      ? [
          {
            title: <span>{strings.switch_indicators_used}</span>,
            dataIndex: 'switchIndicators',
            width: 70,
            render: (switchIndicators) => (
              <span>
                {switchIndicators == null
                  ? strings.n_a
                  : switchIndicators.length}
              </span>
            ),
          },
        ]
      : []),
  ]

  const addToSearchRequestBody = {
    serial: serialNumber,
    filterConflicts: true,
    includeRevisionQueryCounts: true,
  }

  return (
    <>
      <AdvancedSearch
        searchUri="features/search"
        columns={getTableColumns()}
        collectionName={!showFastFeatureFilters ? FEATURE : FAST_FEATURES}
        labelText={strings.feature_search}
        labelType={strings.label_text}
        serialNumber={serialNumber}
        isHorizontalLayout
        addToSearchRequestBody={addToSearchRequestBody}
        allowMultiSelect
        searchResults={searchResults}
      />
    </>
  )
}

AddRemoveFeatureSearchAndResultsTable.propTypes = {
  onSelected: PropTypes.func.isRequired,
  onSelectedRevision: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  features: PropTypes.array.isRequired,
  serialNumber: PropTypes.string.isRequired,
  selected: PropTypes.array,
  modules: PropTypes.array,
  authenticatedModuleNames: PropTypes.array,
  setModalViewForFeature: PropTypes.func,
  showFastFeatureFilters: PropTypes.bool,
}

export default AddRemoveFeatureSearchAndResultsTable
